import { useEffect, useEffectEvent, useRef, type Dispatch } from 'react';
import type { OfferFares } from '../results/offer/offer_list.js';
import type { FlightExplorerOffer } from './search_source.js';
import {
  activeOffers,
  isFaresStage,
  type ExplorerEvent,
  type ExplorerState,
} from './explorer_state.js';

/**
 * Degelo do voltar-do-checkout (#2114): a MUDANÇA do `resumeSeq` (nunca o valor
 * do mount — o ref nasce nele) derruba o `confirmed` via `unconfirmed` e
 * re-arma o `sentRef` do single-fire (`takePickFor`). O reducer preserva
 * picks/listas/filtro. Ver o contrato do evento em `explorer_state.ts`.
 *
 * E devolve a pessoa AONDE ela estava (#3239 C6): a escada FECHOU no pick
 * (`fares.collapse()`) e o drawer do checkout, ao desmontar, não tinha mais a
 * quem devolver o foco — o Base UI só restaura para um elemento ainda no DOM, e
 * o "Confirmar" da escada tinha saído com ela; o foco caía no primeiro focável
 * da coluna, o combobox Ordenar, com a escolha fora de vista. Agora a escada da
 * oferta escolhida reabre (a seleção de tarifa segue lá) e o foco vai para o
 * toggle dela — na fase de tarifas do ida-e-volta, para o título da página. O
 * foco espera um frame: o restore do Base UI corre num microtask depois do
 * unmount e, sem a espera, venceria o nosso.
 *
 * Hook próprio pelo teto de `max-lines-per-function` do componente; fora do
 * `explorer.tsx` pelo teto de linhas do arquivo.
 */
export function useResumeSeq(
  resumeSeq: number | undefined,
  sentRef: { current: boolean },
  send: Dispatch<ExplorerEvent>,
  state: ExplorerState,
  fares: OfferFares<FlightExplorerOffer>,
): void {
  const lastResumeRef = useRef(resumeSeq ?? 0);
  // `state`/`fares` lidos como evento, FORA das deps: os dois mudam já no render
  // seguinte (o `unconfirmed` reforma o estado; `fares` é um objeto novo por
  // render) e, como deps, o cleanup cancelaria o frame antes de ele disparar.
  const focusTarget = useEffectEvent(() =>
    resumeFocusTarget({ ...state, confirmed: false }, fares),
  );
  useEffect(() => {
    const seq = resumeSeq ?? 0;
    if (seq === lastResumeRef.current) return undefined;
    lastResumeRef.current = seq;
    sentRef.current = false;
    send({ t: 'unconfirmed' });
    // O alvo é lido no estado DEPOIS do degelo: com o pick final o `confirmed`
    // é o que segura a tela de tarifas em cena (`isFaresStage`), e no só ida e
    // no multi ela sai com ele — o foco tem de pousar na linha, não num título
    // que não vai existir no frame seguinte.
    const target = focusTarget();
    const frame = requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(target)?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [resumeSeq, send, sentRef]);
}

/** O seletor do elemento que recebe o foco no degelo — e o efeito colateral de
 *  reabrir a escada da oferta escolhida quando a fase é de lista. `data-offer-id`
 *  é único por oferta, então o seletor não precisa de raiz. */
function resumeFocusTarget(state: ExplorerState, fares: OfferFares<FlightExplorerOffer>): string {
  if (isFaresStage(state)) return '.ffs-title';
  const pickedId = state.chosen[state.activeLeg]?.offerId;
  const picked = pickedId ? activeOffers(state).find((o) => o.offerId === pickedId) : undefined;
  if (!picked) return '.fex-results';
  if (fares.enabled && fares.expandedId !== picked.offerId) fares.toggle(picked);
  return `[data-offer-id="${picked.offerId}"] .fq-fares-cta`;
}
